import { map } from "lodash";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { database, storage } from "../../config/firebaseConfig";

export const fetchMaintenanceLogs = createAsyncThunk(
  "logs/maintenance",
  async (id, thunkAPI) => {
    try {
      if (!id) {
        await database.ref("logs/maintenance").on("value", (snapshot) => {
          return thunkAPI.dispatch(fetchMaintenance(snapshot.val()));
        });
      } else {
        await database.ref(`logs/maintenance/${id}`).on("value", (snapshot) => {
          return thunkAPI.dispatch(fetchMaintenance({ [id]: snapshot.val() }));
        });
      }
      return;
    } catch (e) {
      throw e;
    }
  }
);

export const fetchUTTLogs = createAsyncThunk(
  "logs/utt",
  async (id, thunkAPI) => {
    try {
      if (!id) {
        await database.ref("logs/utt/items").on("value", (snapshot) => {
          return thunkAPI.dispatch(fetchUTT(snapshot.val()));
        });
      } else {
        await database.ref(`logs/utt/items/${id}`).on("value", (snapshot) => {
          return thunkAPI.dispatch(fetchUTT({ [id]: snapshot.val() }));
        });
      }
      return;
    } catch (e) {
      throw e;
    }
  }
);

export const cancelFetchUTTLogs = createAsyncThunk(
  "logs/uttOff",
  async (_, thunkAPI) => {
    try {
      await database.ref("logs/utt/items").off();
      return;
    } catch (e) {
      throw e;
    }
  }
);

export const fetchHydroLogs = createAsyncThunk(
  "logs/hydro",
  async (id, thunkAPI) => {
    try {
      if (!id) {
        await database
          .ref("logs/hydrotest/testItems")
          .on("value", (snapshot) => {
            return thunkAPI.dispatch(fetchHydro(snapshot.val()));
          });
      } else {
        await database
          .ref(`logs/hydrotest/testItems/{id}`)
          .on("value", (snapshot) => {
            return thunkAPI.dispatch(fetchHydro({ [id]: snapshot.val() }));
          });
      }
      return;
    } catch (e) {
      throw e;
    }
  }
);

export const cancelFetchHydroLogs = createAsyncThunk(
  "logs/hydro",
  async (_, thunkAPI) => {
    try {
      await database.ref("logs/hydrotest/testItems").off();
      return;
    } catch (e) {
      throw e;
    }
  }
);

// Windowed live view ordered by the `date` child — item keys are NOT
// uniformly chronological push ids (a custom key scheme in the node's
// history makes key-order windows surface old entries), so the date field
// is the only trustworthy recency axis. With ".indexOn": "date" on
// logs/mpi/items the server does the cut; without it the SDK falls back
// to syncing the node and sorting client-side (the page's old cost, no
// worse), and upgrades silently once the index is added.
export const DEFAULT_MPI_WINDOW = 1500;
export const MPI_WINDOW_STEP = 3000;

let mpiQuery = null;
let mpiLimit = 0;

export const fetchMPILogs = createAsyncThunk(
  "logs/mpi",
  async (limitArg, thunkAPI) => {
    try {
      const limit = limitArg || DEFAULT_MPI_WINDOW;
      if (mpiQuery && limit <= mpiLimit) return;
      if (mpiQuery) mpiQuery.off();
      mpiLimit = limit;
      mpiQuery = database
        .ref("logs/mpi/items")
        .orderByChild("date")
        .limitToLast(limit);
      mpiQuery.on("value", (snapshot) => {
        return thunkAPI.dispatch(fetchMPI(snapshot.val()));
      });
      return;
    } catch (e) {
      throw e;
    }
  }
);

export const fetchRebuildLogs = createAsyncThunk(
  "logs/rebuild",
  async (id, thunkAPI) => {
    try {
      if (!id) {
        await database.ref("logs/rebuild/items").on("value", (snapshot) => {
          return thunkAPI.dispatch(fetchRebuild(snapshot.val()));
        });
      } else {
        await database
          .ref(`logs/rebuild/items/${id}`)
          .on("value", (snapshot) => {
            return thunkAPI.dispatch(fetchRebuild({ [id]: snapshot.val() }));
          });
      }
      return;
    } catch (e) {
      throw e;
    }
  }
);

export const cancelFetchRebuildLogs = createAsyncThunk(
  "logs/rebuildOff",
  async () => {
    try {
      await database.ref("logs/rebuild/items").off();
      return;
    } catch (e) {
      throw e;
    }
  }
);
// Delete multiple rebuild log records by id
export const deleteRebuildRecords = createAsyncThunk(
  "logs/deleteRebuildRecords",
  async (ids = [], { rejectWithValue }) => {
    try {
      const updates = {};
      ids.forEach((id) => {
        updates[`/logs/rebuild/items/${id}`] = null; // RTDB multi-path delete
      });

      await database.ref().update(updates);
      return ids;
    } catch (err) {
      return rejectWithValue(err?.message || String(err));
    }
  }
);

export const cancelFetchMPILogs = createAsyncThunk(
  "logs/mpiOff",
  async (_, thunkAPI) => {
    try {
      if (mpiQuery) {
        mpiQuery.off();
        mpiQuery = null;
        mpiLimit = 0;
      }
      return;
    } catch (e) {
      throw e;
    }
  }
);

export const cancelFetchMaintenaceLogs = createAsyncThunk(
  "logs/maintenanceOff",
  async (_, thunkAPI) => {
    try {
      await database.ref("logs/maintenance").off();
      return;
    } catch (e) {
      throw e;
    }
  }
);

const slice = createSlice({
  name: "logs",
  initialState: {
    maintenance: {},
    utt: {},
    mpi: {},
    mpiLoaded: false,
    hydro: {},
    rebuild: {},
    status: "idle",
  },
  reducers: {
    fetchMaintenance: (state, action) => {
      state.maintenance = { ...action.payload };
      state.status = "idle";
    },
    fetchUTT: (state, action) => {
      state.utt = { ...action.payload };
      state.status = "idle";
    },
    fetchMPI: (state, action) => {
      state.mpi = { ...action.payload };
      state.mpiLoaded = true;
      state.status = "idle";
    },
    fetchHydro: (state, action) => {
      state.hydro = { ...action.payload };
      state.status = "idle";
    },
    fetchRebuild: (state, action) => {
      state.rebuild = { ...action.payload };
      state.status = "idle";
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchMaintenanceLogs.pending, (state) => {
          state.status = "pending";
        })
        .addCase(fetchMaintenanceLogs.fulfilled, (state, action) => {
          state.status = "idle";
          // state.logs.maintenance = action.payload
        })
        .addCase(fetchHydroLogs.pending, (state) => {
          state.status = "pending";
        })
        .addCase(fetchHydroLogs.fulfilled, (state, action) => {
          state.status = "idle";
          // state.logs.maintenance = action.payload
        })
        .addCase(fetchUTTLogs.pending, (state) => {
          state.status = "pending";
        })
        .addCase(fetchUTTLogs.fulfilled, (state, action) => {
          state.status = "idle";
          // state.logs.maintenance = action.payload
        })
        .addCase(fetchRebuildLogs.pending, (state) => {
          state.status = "pending";
        })
        .addCase(fetchRebuildLogs.fulfilled, (state) => {
          state.status = "idle";
        });
    },
  },
});

export default slice.reducer;

const { fetchMaintenance, fetchUTT, fetchMPI, fetchHydro, fetchRebuild } =
  slice.actions;
